import { ReactNode, useContext, useState, useEffect, Suspense } from "react"
import { getChildByType } from "@/lib/children"

// DateTime
import { DateTime } from "@/lib/dates"
import { dateFormat } from "@/constants/constants"

// UI
import { cn } from "@/lib/utils"
import {
	Popover,
	PopoverGroup,
	PopoverPanel,
	PopoverButton,
	Transition,
} from "@/lib/ui"
import { Button } from "@/components/Button"
import { ChevronDownIcon } from "@/lib/icons"
import { DatePicker } from "@/components/form-controls/DatePicker"
import { transformToDatePickerDate } from "@/components/form-controls/datePicker.utils"
import { CardBody } from "@/components/Card"
import { GraphHeader } from "@/components/graphs/header/GraphHeader"
import { EmptyGridStateAnimated } from "@/components/EmptyGridState"

// Animations
import { getTransitionPopoverProps } from "@/lib/animations"

// Translations
import { useTrans } from "@/i18n"

// Context
import { FinancePerformanceContext } from "@/pages/finance/pages/PerformanceContext"
import {
	ProductionCumulativeContext,
	useProductionCumulativeGraphContext,
	AvailableDatePeriods,
	type GraphIntervalType,
} from "./ProductionCumulativeContext"

type ProductionCumulativeProps = {
	children: ReactNode
}

/// useFinancePerformanceContextOrState
const useFinancePerformanceContextOrState = () => {
	const context = useContext(FinancePerformanceContext)

	const [localStartTime, setLocalStartTime] = useState(
		DateTime.local().toJSDate(),
	)
	const [localEndTime, setLocalEndTime] = useState(
		DateTime.local().toJSDate(),
	)

	if (context) {
		return {
			startTime: context.selectedDateRangeStart,
			setStartTime: context.setSelectedDateRangeStart,
			endTime: context.selectedDateRangeEnd,
			setEndTime: context.setSelectedDateRangeEnd,
		}
	} else {
		return {
			startTime: localStartTime,
			setStartTime: setLocalStartTime,
			endTime: localEndTime,
			setEndTime: setLocalEndTime,
		}
	}
}

/**
 * ProductionCumulative
 * @param param0
 * @returns
 */
export function ProductionCumulative({ children }: ProductionCumulativeProps) {
	const content = getChildByType(children, ProductionCumulative.Content)
	const footer = getChildByType(children, ProductionCumulative.Footer)
	const t = useTrans("project")

	// State
	const [graphInterval, setGraphInterval] =
		useState<GraphIntervalType>("year")
	const [minimumTime, setMinimumTime] = useState(DateTime.local().toJSDate())

	// Hooks
	const { startTime, setStartTime, endTime, setEndTime } =
		useFinancePerformanceContextOrState()

	// When changing graphInterval, update dates
	useEffect(() => {
		// YEAR
		if (graphInterval === AvailableDatePeriods.YEAR) {
			setStartTime(
				DateTime.local()
					.toUTC()
					.startOf("year")
					.minus({ day: 1 }) // Subtract 1 day because data points are cummulated at the end of every month
					.toJSDate(),
			)
			setEndTime(DateTime.local().toUTC().endOf("year").toJSDate())
		}

		// MONTH
		if (graphInterval === AvailableDatePeriods.MONTH) {
			setStartTime(DateTime.local().toUTC().startOf("month").toJSDate())
			setEndTime(DateTime.local().toUTC().endOf("month").toJSDate())
		}

		// ALL
		if (graphInterval === AvailableDatePeriods.ALL) {
			setStartTime(minimumTime)
			setEndTime(DateTime.local().startOf("day").toJSDate())
		}
	}, [graphInterval, minimumTime])

	return (
		<ProductionCumulativeContext.Provider
			value={{
				graphInterval,
				setGraphInterval,
				startTime,
				setStartTime,
				endTime,
				setEndTime,
				minimumTime,
				setMinimumTime,
			}}
		>
			<CardBody className="lg:flex lg:items-center lg:justify-between">
				<div className="w-full lg:w-72">
					<GraphHeader
						title={t("project:data.energy_producted.title")}
						subtitle={t("project:data.energy_producted.subtitle")}
					/>
				</div>
				<div className="mt-4 flex flex-col gap-4 md:flex-row lg:mt-0">
					{/** DateRangePicker */}
					<DateRangePicker />

					{/** Buttons */}
					<div>
						{Object.values(AvailableDatePeriods).map(
							(interval, index) => {
								return (
									<Button
										key={interval}
										onClick={() =>
											setGraphInterval(
												interval as GraphIntervalType,
											)
										}
										className={cn(
											index === 3
												? "rounded-l-none border-l-0"
												: index === 0
													? "rounded-r-none"
													: "rounded-none",
											graphInterval === interval &&
												"relative z-10",
										)}
										variant={
											graphInterval === interval
												? "primary"
												: "transparent"
										}
									>
										{t(
											`project:data.energy_producted.button.${interval}`,
										)}
									</Button>
								)
							},
						)}
					</div>
				</div>
			</CardBody>
			<div className="relative h-96">
				<Suspense
					fallback={
						<EmptyGridStateAnimated className={"h-[30%] w-full"} />
					}
				>
					{content}
				</Suspense>
			</div>
			{footer}
		</ProductionCumulativeContext.Provider>
	)
}

const Content = ({ children }: { children: ReactNode }) => {
	return <>{children}</>
}
Content.displayName = "ProductionCumulative.Content"

const Footer = ({ children }: { children: ReactNode }) => {
	return <>{children}</>
}
Footer.displayName = "ProductionCumulative.Footer"

ProductionCumulative.Content = Content
ProductionCumulative.Footer = Footer

/**
 * DateRangePicker
 * @param param0
 * @returns
 */
function DateRangePicker({ className }: { className?: string }) {
	// Context
	const { startTime, setStartTime, endTime, setEndTime } =
		useProductionCumulativeGraphContext()

	return (
		<div className={cn("flex items-center gap-4", className)}>
			<PopoverGroup className="flex items-center divide-x divide-gray-200">
				<Popover className="relative z-20 inline-block text-left">
					<PopoverButton
						className={cn(
							"group inline-flex items-center justify-center rounded-md",
							"border border-gray-200 bg-white px-4 py-2 text-sm text-gray-500",
							"focus:ring-primary-500 hover:bg-gray-50 hover:text-gray-900 focus:ring-2 focus:outline-hidden",
						)}
					>
						<span className="mr-2">
							{DateTime.fromJSDate(startTime).toFormat(
								dateFormat,
							)}
						</span>

						<ChevronDownIcon
							className="ml-2 size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
							aria-hidden="true"
						/>
					</PopoverButton>

					<Transition {...getTransitionPopoverProps()}>
						<PopoverPanel className="absolute left-0 mt-2 origin-top-left rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5 focus:outline-hidden">
							<div
								className="min-w-[300px]"
								data-testid="daterangepicker-from"
							>
								<DatePicker
									selectedDate={transformToDatePickerDate(
										startTime,
									)}
									onSelectDate={(date) => {
										const newDate =
											DateTime.fromObject(date).endOf(
												"day",
											)
										setStartTime(newDate.toJSDate())
									}}
								/>
							</div>
						</PopoverPanel>
					</Transition>
				</Popover>
			</PopoverGroup>
			<span className="text-sm text-gray-500">to</span>
			<PopoverGroup className="flex items-center divide-x divide-gray-200">
				<Popover className="relative z-20 inline-block text-left">
					<PopoverButton
						className={cn(
							"group inline-flex items-center justify-center rounded-md",
							"border border-gray-200 bg-white px-4 py-2 text-sm text-gray-500",
							"focus:ring-primary-500 hover:bg-gray-50 hover:text-gray-900 focus:ring-2 focus:outline-hidden",
						)}
					>
						<span className="mr-2">
							{DateTime.fromJSDate(endTime).toFormat(dateFormat)}
						</span>
						<ChevronDownIcon
							className="ml-2 size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
							aria-hidden="true"
						/>
					</PopoverButton>

					<Transition {...getTransitionPopoverProps()}>
						<PopoverPanel className="absolute left-0 mt-2 origin-top-left rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5 focus:outline-hidden">
							<div
								className="min-w-[300px]"
								data-testid="daterangepicker-from"
							>
								<DatePicker
									selectedDate={transformToDatePickerDate(
										endTime,
									)}
									onSelectDate={(date) => {
										const newDate =
											DateTime.fromObject(date).endOf(
												"day",
											)
										setEndTime(newDate.toJSDate())
									}}
								/>
							</div>
						</PopoverPanel>
					</Transition>
				</Popover>
			</PopoverGroup>
		</div>
	)
}
